import {
  type FrontendInstallContext,
  type FrontendInstaller,
  otherInstalledIntegration,
} from "@p0security/integrations-installer";
import type { JiraAiComponents } from "@p0security/integrations-jira-ai-shared/components";
import { toGcpConnectorServiceAccount } from "@p0security/integrations-jira-ai-shared/connector-hosting";
import { isGcpProjectId } from "@p0security/integrations-jira-ai-shared/gcp-hosting";
import {
  INVALID_ITEM_ID,
  INVALID_SITE_ID,
} from "@p0security/integrations-jira-ai-shared/item-id";
import { isGcpSecretName } from "@p0security/integrations-jira-ai-shared/secret-name";
import { INVALID_SITE_URL } from "@p0security/integrations-jira-ai-shared/site-url";
import type {
  JiraAiConnectorConfig,
  JiraAiIntegration,
  JiraAiPoolAccountConfig,
  JiraAiSiteConfig,
} from "@p0security/integrations-jira-ai-shared/types";
import {
  p0InvokerEmail,
  shellLines,
} from "@p0security/integrations-vaulted-credential-shared/iac";
import {
  installedIds,
  installedItem,
  installedItems,
} from "@p0security/shared/install/installed";
import type { Instructions } from "@p0security/shared/install/types";
import type { NewConfig } from "@p0security/shared/integrations/shared";
import { ValidationError } from "@p0security/shared/types/error";
import { assertNeverError } from "@p0security/shared/types/index";
import { Typography } from "@p0security/ui";
import type { ReactNode } from "react";

import {
  type IacCommand,
  renderGcpConnector,
  renderSiteGrants,
} from "./iac.tsx";

export type JiraAiInstaller = FrontendInstaller<typeof JiraAiComponents>;

type JiraAiInstallContext = Readonly<FrontendInstallContext<JiraAiIntegration>>;

const { Paragraph, Text } = Typography;

/** What a setup page shows in place of Terraform that couldn't be rendered.
 * Copied from Harbor's `toRefusal`, in
 * packages/integrations/harbor/frontend/src/index.tsx.
 *
 * A `ValidationError` is a refusal, whose message tells the customer what to
 * do. Anything else is P0's own mistake: it's logged as an error, and the
 * page says to contact P0, rather than taking the whole install page down.
 */
const toRefusal = (error: unknown, item: string): ReactNode => {
  if (error instanceof ValidationError) {
    return <Paragraph type="warning">{error.message}</Paragraph>;
  }
  console.error("P0 couldn't build the Terraform of the Jira %s", item, error);
  return (
    <Paragraph type="warning">
      P0 couldn&apos;t build this Terraform. That&apos;s a problem on P0&apos;s
      side: contact support@p0.dev.
    </Paragraph>
  );
};

/** A setup page with one Terraform snippet under `help`, or the refusal in
 * its place. */
const toSetup = (args: {
  help: ReactNode;
  item: string;
  render: () => IacCommand;
}): Instructions<ReactNode> => {
  const { help, item, render } = args;
  try {
    return { commands: { iac: [render()] }, help };
  } catch (error) {
    return {
      help: (
        <>
          {toRefusal(error, item)}
          {help}
        </>
      ),
    };
  }
};

/** The service account a connector runs as: the one P0 stored, or, before
 * then, the one named after the connector. */
const findServiceAccount = (hosting: JiraAiConnectorConfig["hosting"]) => {
  const { connectorName, connectorServiceAccount, projectId } = hosting;
  if (typeof connectorServiceAccount === "string" && connectorServiceAccount) {
    return connectorServiceAccount;
  }
  return typeof connectorName === "string" &&
    connectorName &&
    typeof projectId === "string" &&
    projectId
    ? toGcpConnectorServiceAccount({ connectorName, projectId })
    : undefined;
};

const connectorInstructions: JiraAiInstaller["components"]["connector"]["instructions"] =
  (context, id, item): Instructions<ReactNode> => {
    const serviceAccount = findServiceAccount(item.hosting);
    return toSetup({
      help: (
        <>
          <Paragraph>
            Apply the Terraform below to deploy the connector, then click Next.
            P0 checks that it can reach the connector.
          </Paragraph>
          {serviceAccount ? (
            <Paragraph>
              Service account: <Text code>{serviceAccount}</Text>. The connector
              runs as it, and each Jira site that uses the connector grants it
              what it needs in that site&apos;s secrets project.
            </Paragraph>
          ) : null}
        </>
      ),
      item: `connector ${id}`,
      render: () =>
        renderGcpConnector({
          hosting: item.hosting,
          id,
          invokerEmail: p0InvokerEmail(context.installed),
        }),
    });
  };

/** The installed connector item `connectorId`, if any. */
const findConnector = (context: JiraAiInstallContext, connectorId: unknown) =>
  context.config && typeof connectorId === "string"
    ? installedItem("connector", context.config, connectorId)
    : undefined;

const siteInstructions: JiraAiInstaller["components"]["site"]["instructions"] =
  (context, id, item): Instructions<ReactNode> => {
    const connector = findConnector(context, item.connector);
    if (!connector) {
      return {
        help: (
          <Text type="warning">
            Install a connector with the ID {item.connector}, or finish
            installing it, then return here.
          </Text>
        ),
      };
    }
    return toSetup({
      help: (
        <Paragraph>
          If you haven&apos;t yet, store the admin OAuth client&apos;s secret as
          the whole value of the secret <Text code>{item.adminSecretName}</Text>{" "}
          in Google Secret Manager. Then apply the Terraform below so that the
          connector can use the site&apos;s secrets, and click Next.
        </Paragraph>
      ),
      item: `site ${id}`,
      render: () =>
        renderSiteGrants({
          connectorId: item.connector,
          hosting: connector.hosting,
          id,
          site: item,
        }),
    });
  };

/** The site item `siteId`, at any state, if any. Not `site[siteId]`: an id
 * may be the name of something every object inherits, such as
 * `constructor`. */
const findSite = (
  context: JiraAiInstallContext,
  siteId: unknown
): JiraAiSiteConfig | undefined => {
  const sites = context.config?.site;
  return sites && typeof siteId === "string" && Object.hasOwn(sites, siteId)
    ? sites[siteId]
    : undefined;
};

/** The Google Cloud project that holds a site's secrets, or "" for a site
 * that keeps them anywhere but Google Secret Manager. */
const toSecretsProject = (vault: JiraAiSiteConfig["vault"]) => {
  switch (vault.type) {
    case "gcp-sm":
      return vault.install;
    case "aws-sm":
    case "azure-kv":
      return "";
    default:
      throw assertNeverError(vault);
  }
};

const poolAccountInstructions: JiraAiInstaller["components"]["pool-account"]["instructions"] =
  (context, _id, item: JiraAiPoolAccountConfig): Instructions<ReactNode> => {
    const site = findSite(context, item.site);
    const project = site ? toSecretsProject(site.vault) : "";
    const { secretName } = item;
    // Both go into a shell command, unquoted.
    if (!isGcpProjectId(project) || !isGcpSecretName(secretName)) {
      return {
        help: (
          <Text type="warning">
            P0 can&apos;t name this account&apos;s secret, because its site
            isn&apos;t a Jira site with its secrets in Google Secret Manager.
            Delete this account and add it again on an installed site.
          </Text>
        ),
      };
    }
    return {
      commands: {
        shell: [
          {
            command: [
              'read -rsp "API key: " API_KEY',
              shellLines(
                `printf '%s' "$API_KEY" | gcloud secrets create ${secretName}`,
                `--project=${project}`,
                "--replication-policy=automatic",
                "--data-file=-"
              ),
              "unset API_KEY",
            ].join("\n"),
            header: (
              <Paragraph>
                Creates the secret. Paste the API key at the prompt, which keeps
                it off the screen and out of your shell history.
              </Paragraph>
            ),
          },
        ],
      },
      help: (
        <>
          <Paragraph>
            On the service account&apos;s page in Atlassian Administration,
            create an API token (Create credentials &gt; API token) with the
            scopes <Text code>read:jira:agent-interface</Text>,{" "}
            <Text code>write:jira:agent-interface</Text> and{" "}
            <Text code>search:jira:agent-interface</Text>. If you haven&apos;t
            yet, turn on API tokens for the Atlassian MCP server, in Atlassian
            Administration under Connectors &gt; Atlassian MCP server &gt;
            Authentication &gt; API token.
          </Paragraph>
          <Paragraph>
            Store the API token, as the whole value, in the secret{" "}
            <Text code>{secretName}</Text> in Google Cloud project{" "}
            <Text code>{project}</Text>, then click Next. P0 looks up the
            account by its email with the site&apos;s admin client. P0 never
            reads the secret. Only your connector does.
          </Paragraph>
        </>
      ),
    };
  };

/** The installed items of `component`, for a field that points at one. */
const itemOptions =
  (component: "connector" | "site") =>
  async (context: JiraAiInstallContext) => {
    const { config } = context;
    if (!config) return [];
    return Object.entries(installedItems(component, config))
      .map(([id, item]) => ({
        id,
        label: item?.label ? `${item.label} (${id})` : id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

/** A picker of the installs of another integration's `iam-write`
 * component, whose label says to install one first when there are none.
 * Copied from Harbor's `installPicker`. */
const installPicker = (args: {
  description: string;
  integration: string;
  missing: string;
}) => {
  const { description, integration, missing } = args;
  return {
    labeler: (context: JiraAiInstallContext) => {
      const config = context.installed[integration];
      return config &&
        installedIds<string, NewConfig>("iam-write", config).length > 0
        ? description
        : missing;
    },
    optionProvider: otherInstalledIntegration({
      component: "iam-write",
      integration,
    }),
  };
};

export const jiraAiInstaller: JiraAiInstaller = {
  components: {
    connector: {
      instructions: connectorInstructions,
      items: {
        hosting: {
          labeler: () => "Where the connector runs.",
          options: {
            gcp: {
              items: {
                connectorName: {
                  labeler: () =>
                    "The name of the connector's Cloud Run service, which is also the ID of the service account it runs as.",
                },
                connectorRegion: {
                  labeler: () =>
                    "The Google Cloud region the connector runs in, such as us-west1.",
                },
                projectId: installPicker({
                  description:
                    "The Google Cloud project the connector runs in. P0 lists the projects where its Google Cloud IAM management is installed.",
                  integration: "gcloud",
                  missing:
                    "Install P0's Google Cloud IAM management for the connector's project first. P0 lists only the projects where it's installed.",
                }),
              },
            },
          },
        },
      },
      labeler: () =>
        `A name of your choosing for this connector, unique among your Jira connectors. ${INVALID_ITEM_ID}`,
    },
    site: {
      instructions: siteInstructions,
      items: {
        connector: {
          optionProvider: itemOptions("connector"),
          prerequisiteMessages: async (context) =>
            (await itemOptions("connector")(context)).length === 0
              ? "Install a Jira connector, and finish its setup, first."
              : undefined,
        },
        siteUrl: {
          errorElement: () => INVALID_SITE_URL,
          labeler: () => INVALID_SITE_URL,
        },
        vault: {
          options: {
            "gcp-sm": {
              items: {
                install: installPicker({
                  description:
                    "The Google Cloud project the site's secrets are in. P0 lists the projects where its Google Secret Manager integration is installed.",
                  integration: "gcp-sm",
                  missing:
                    "Install P0's Google Secret Manager integration for the project that holds the site's secrets first.",
                }),
              },
            },
          },
        },
      },
      labeler: () =>
        `A name of your choosing for this Jira site. Agents name it when they request access. ${INVALID_SITE_ID}`,
    },
    "pool-account": {
      instructions: poolAccountInstructions,
      items: {
        site: {
          optionProvider: itemOptions("site"),
          prerequisiteMessages: async (context) =>
            (await itemOptions("site")(context)).length === 0
              ? "Install a Jira site first."
              : undefined,
        },
      },
      labeler: () =>
        `A name of your choosing for this agent account. ${INVALID_ITEM_ID}`,
    },
  },
};
